/**
 * Standard Actions configuration for Dawn.
 *
 * Storefront Renderer injects the Shopify Standard Actions bundle
 * (`window.Shopify.actions.{updateCart,openCart,getCart,…}`). This file
 * overrides the bundle's built-in Dawn refresh path with an explicit,
 * in-theme version so forks that change Dawn's cart contract keep
 * working. Remove this file and the built-in defaults take over.
 *
 *   - openCart   — opens <cart-drawer>; falls back to /cart.
 *   - updateCart — after the Storefront API mutation, refreshes the
 *     affected cart sections and publishes `cart-update` so Dawn's
 *     pubsub subscribers react.
 *   - other actions (getCart, etc.) keep the default implementation.
 */

const DAWN_CART_TAGS = ['cart-drawer', 'cart-items', 'cart-drawer-items', 'cart-notification'];

const DAWN_PUBSUB_REFRESHED_SECTIONS = new Set([
  'cart-drawer:cart-drawer',
  'cart-drawer-items:CartDrawer',
  'cart-items:main-cart-items',
]);

function collectCartSections() {
  const sections = new Map();

  for (const el of document.querySelectorAll(DAWN_CART_TAGS.join(','))) {
    let entries;
    try {
      entries = el.getSectionsToRender?.();
    } catch {
      continue;
    }

    const tag = el.tagName.toLowerCase();
    for (const entry of entries ?? []) {
      if (DAWN_PUBSUB_REFRESHED_SECTIONS.has(`${tag}:${entry.id}`)) continue;

      const sectionId = entry.section ?? entry.id;
      if (!sectionId || sections.has(sectionId)) continue;

      const root = entry.section ? document.getElementById(entry.id) : document;
      if (!root) continue;

      const mount = entry.selector
        ? (root.querySelector(entry.selector) ?? (entry.section ? root : null))
        : document.getElementById(entry.id);
      if (!mount) continue;

      sections.set(sectionId, {
        mount,
        extractSelector: entry.selector || '.shopify-section',
      });
    }
  }

  return sections;
}

async function refreshDawnCartUI() {
  const sections = collectCartSections();
  const sectionsQuery = sections.size
    ? `?sections=${[...sections.keys()].join(',')}`
    : '';
  const cartUrl = (typeof routes !== 'undefined' && routes?.cart_url) || '/cart';
  const url = `${cartUrl}.js${sectionsQuery}`;
  const cartData = await fetch(url, { headers: { Accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);

  if (cartData?.sections) {
    for (const [id, { mount, extractSelector }] of sections) {
      const html = cartData.sections[id];
      if (!html) continue;
      const source = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(extractSelector);
      if (source) mount.replaceChildren(...source.childNodes);
    }
  }

  publish(PUB_SUB_EVENTS.cartUpdate, {
    source: 'external-refresh',
    cartData: cartData ?? undefined,
  });
}

function initStandardActions() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.openCart.configure({
    async handler(defaultHandler) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        return;
      }
      return defaultHandler();
    },
  });

  actions.updateCart.configure({
    eventTarget: () => document,
    async handler(defaultHandler) {
      const result = await defaultHandler();
      try {
        await refreshDawnCartUI();
      } catch (error) {
        console.error('[Dawn] Standard Actions cart refresh failed; reloading.', error);
        window.location.reload();
      }
      return result;
    },
  });
}

if (window.Shopify?.actions) {
  initStandardActions();
} else {
  document.addEventListener('DOMContentLoaded', initStandardActions, { once: true });
}

/**
 * PUTAOYAO product-interest tracking.
 * Counts a storefront click into a product once per browser + product + 60 minutes.
 * The dedupe state stays in the visitor's browser. No generated visitor ID is transmitted.
 * Valid events are published to Shopify Analytics and, when present, Google Tag Manager/GA4 dataLayer.
 */
(function initPutaoyaoProductInterestTracking() {
  const DEDUPE_KEY = 'putaoyaoProductClickDedupeV1';
  const DEDUPE_MS = 60 * 60 * 1000;
  const RETENTION_MS = 7 * 24 * DEDUPE_MS;

  function productHandleFromUrl(rawHref) {
    if (!rawHref) return '';
    try {
      const url = new URL(rawHref, window.location.origin);
      if (url.origin !== window.location.origin) return '';
      const match = url.pathname.match(/\/products\/([^/?#]+)/i);
      return match ? decodeURIComponent(match[1]) : '';
    } catch (_) {
      return '';
    }
  }

  function readDedupeMap() {
    try {
      const parsed = JSON.parse(localStorage.getItem(DEDUPE_KEY) || '{}');
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (_) {
      return {};
    }
  }

  function writeDedupeMap(map, now) {
    try {
      for (const key of Object.keys(map)) {
        if (now - Number(map[key] || 0) > RETENTION_MS) delete map[key];
      }
      localStorage.setItem(DEDUPE_KEY, JSON.stringify(map));
    } catch (_) {}
  }

  function publishValidProductClick(anchor, handle) {
    const payload = {
      product_handle: handle,
      target_path: anchor.pathname || anchor.getAttribute('href') || '',
      source_path: window.location.pathname,
      source_template: document.querySelector('main[data-template]')?.dataset.template || '',
      locale: document.documentElement.lang || '',
      dedupe_window_minutes: 60,
    };

    try {
      window.Shopify?.analytics?.publish?.('putaoyao_product_click', payload);
    } catch (_) {}

    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'putaoyao_product_click', ...payload });
    } catch (_) {}
  }

  document.addEventListener('click', (event) => {
    if (event.defaultPrevented) return;
    const anchor = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!anchor) return;

    const handle = productHandleFromUrl(anchor.href);
    if (!handle) return;

    const now = Date.now();
    const map = readDedupeMap();
    const last = Number(map[handle] || 0);
    if (last && now - last < DEDUPE_MS) return;

    map[handle] = now;
    writeDedupeMap(map, now);
    publishValidProductClick(anchor, handle);
  }, { capture: true });
})();
